import React, { useEffect, useRef } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import cx from 'classnames';
import { History } from 'history';

import { MDText } from 'i18n-react';

import { Button, CrossIcon } from '@motorway/mw-highway-code';
import { Hyperlink } from '@motorway/mw-highway-code/alpha';
import { BUTTON_AS_TYPE, SIZE } from '@motorway/mw-highway-code/enums';

import { getCookieValue, setCookie } from 'Utilities';
import { useFullState } from 'Utilities/hooks';

import { Content } from 'LegacySBCRA/Content/Content';

import LocalTexts from './CookieWarningTexts.json';

import styles from './CookieWarning.scss';

const LocalT = new MDText(LocalTexts);

const CookieWarning: React.FC<RouteComponentProps> = ({ history }: { history: History }) => {
  const [state, setState] = useFullState({
    dismissed: false,
    hasCookie: false,
    loaded: false,
  });

  const historyRef = useRef(history);
  const historyUnlistenRef = useRef<(() => void) | undefined>(undefined);

  const dismiss = useRef(() => {
    const { hasCookie } = state;

    setState({
      dismissed: true,
    });

    if (!hasCookie) {
      setCookie('cookieConsent', 'true', 365);
    }

    historyUnlistenRef.current?.();
  });

  useEffect(() => {
    const hasCookie = getCookieValue('cookieConsent') === 'true';

    setState({
      dismissed: hasCookie,
      hasCookie,
      loaded: true,
    });

    if (hasCookie) {
      return () => {};
    }

    historyUnlistenRef.current = historyRef.current.listen(() => {
      dismiss.current();
      historyUnlistenRef.current?.();
    });

    return () => historyUnlistenRef.current?.();
  }, [setState]);

  const { dismissed, loaded } = state;

  if (dismissed || !loaded) {
    return null;
  }

  return (
    <Content
      center
      className={styles.component}
      element="section"
      wrapper={{ className: cx(styles.wrapper) }}
    >
      <div data-testid="cookie-warning">
        {LocalT.translate('text', {
          link: (
            <Hyperlink
              inherit
              as={BUTTON_AS_TYPE.ANCHOR}
              data-testid="cookie-link"
              href="/cookies"
              label={LocalT.translate('cookiePolicy')}
              size={SIZE.MEDIUM}
              target="_blank"
            />
          ),
        })}
      </div>
      <div className={styles.button}>
        <Button
          fullWidth
          className={styles.dismiss}
          icon={CrossIcon}
          label='Dismiss cookie warning'
          onClick={dismiss.current}
          showLabel={false}
          type='button'
        />
      </div>
    </Content>
  );
};

export default withRouter(CookieWarning);
